import type { CSSProperties } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Segmentado } from '#components/mobile/ui/Segmentado';
import { color, radio, TACTIL, texto } from '#components/mobile/ui/tokens';

import { LARGO_CARTA, LARGO_FIRMA, LARGO_SI_ENTONCES } from './recuerdos';
import type { LineaFirmada } from './recuerdos';
import { quedan } from './travesias';

/** Lo que se escribe al zarpar (todo opcional). */
export type DatosCarta = {
  carta: string;
  siEntonces: string;
  /** «Abrir solo al llegar» o «Guardar cerrada hasta…». */
  abrir: 'llegar' | 'hasta';
  /** `AAAA-MM-DD` (solo con `abrir: 'hasta'`). */
  abrirDesde: string;
  /** «Firmar también»: la línea de este dispositivo ('' sin firmar). */
  firma: string;
  /** El campo de la firma está abierto. */
  firmando: boolean;
};

export function cartaVacia(abrirDesde: string): DatosCarta {
  return {
    carta: '',
    siEntonces: '',
    abrir: 'llegar',
    abrirDesde,
    firma: '',
    firmando: false,
  };
}

type SeccionCartaProps = {
  valor: DatosCarta;
  onChange: (cambios: Partial<DatosCarta>) => void;
  /** Barco compartido: aviso y «Firmar también». */
  compartido: boolean;
  /** Firmas ya guardadas (de otros dispositivos). */
  firmas?: readonly LineaFirmada[];
  /** Primer día que se puede elegir en «Guardar cerrada hasta…». */
  minAbrir: string;
};

const estiloCampo: CSSProperties = {
  width: '100%',
  minHeight: TACTIL + 4,
  borderRadius: radio.boton,
  backgroundColor: color.surface2,
  border: `1px solid ${color.line}`,
  padding: '12px 14px',
  fontSize: 16,
  lineHeight: 1.4,
  color: color.fg,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  outline: 'none',
};

const estiloEtiqueta: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 700,
  color: color.fg2,
  margin: '0 2px 6px',
};

/**
 * La carta de la travesía («La botella y la bitácora», F1 §2): una nota para
 * el día de llegada, la línea «Cuando…, aparto…» (Gavi solo la recuerda) y
 * cuándo se puede abrir. En un barco compartido avisa de que quien navegue
 * el barco podrá leerla y deja «Firmar también».
 */
export function SeccionCarta({
  valor,
  onChange,
  compartido,
  firmas = [],
  minAbrir,
}: SeccionCartaProps) {
  const { t } = useTranslation();
  const restantes = quedan(valor.carta);
  return (
    <View style={{ gap: 14 }} data-testid="seccion-carta">
      <View>
        <label htmlFor="travesia-carta" style={estiloEtiqueta}>
          <Trans>A note for the day you arrive</Trans>
        </label>
        <textarea
          id="travesia-carta"
          data-testid="travesia-carta"
          value={valor.carta}
          maxLength={LARGO_CARTA}
          rows={3}
          placeholder={t('Why do you want this?')}
          onChange={e => onChange({ carta: e.target.value })}
          style={{ ...estiloCampo, resize: 'vertical', minHeight: 92 }}
        />
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 8,
            marginTop: 4,
          }}
        >
          <Text style={{ ...texto.secundario, color: color.fg3, flex: 1 }}>
            {compartido ? (
              <Trans>Whoever sails this boat will be able to read it</Trans>
            ) : (
              <Trans>Optional. Only you will read it.</Trans>
            )}
          </Text>
          <Text
            style={{ ...texto.secundario, color: color.fg3, flexShrink: 0 }}
            aria-live="polite"
          >
            {restantes}
          </Text>
        </View>
      </View>

      {compartido && (
        <View style={{ gap: 8 }}>
          {firmas.map(f => (
            <Text
              key={f.disp}
              style={{ ...texto.secundario, color: color.fg2 }}
              data-testid="travesia-firma-guardada"
            >
              {f.quien ? `${f.texto} — ${f.quien}` : f.texto}
            </Text>
          ))}
          {valor.firmando ? (
            <View>
              <label htmlFor="travesia-firma" style={estiloEtiqueta}>
                <Trans>Your line</Trans>
              </label>
              <input
                id="travesia-firma"
                data-testid="travesia-firma"
                value={valor.firma}
                maxLength={LARGO_FIRMA}
                autoComplete="off"
                placeholder={t('A line of your own')}
                onChange={e => onChange({ firma: e.target.value })}
                style={estiloCampo}
              />
            </View>
          ) : (
            <Button
              variant="bare"
              data-testid="travesia-firmar"
              onPress={() => onChange({ firmando: true })}
              style={{
                alignSelf: 'flex-start',
                minHeight: TACTIL,
                padding: '0 14px',
                borderRadius: radio.boton,
                backgroundColor: color.surface2,
                color: color.accent,
                fontWeight: 800,
                fontSize: 14,
              }}
            >
              <Trans>Sign too</Trans>
            </Button>
          )}
        </View>
      )}

      <View>
        <label htmlFor="travesia-si-entonces" style={estiloEtiqueta}>
          <Trans>When…, I set aside…</Trans>
        </label>
        <input
          id="travesia-si-entonces"
          data-testid="travesia-si-entonces"
          value={valor.siEntonces}
          maxLength={LARGO_SI_ENTONCES}
          autoComplete="off"
          placeholder={t('When I get paid, I set aside €100')}
          onChange={e => onChange({ siEntonces: e.target.value })}
          style={estiloCampo}
        />
        <Text
          style={{
            ...texto.secundario,
            color: color.fg3,
            marginTop: 4,
            display: 'block',
          }}
        >
          <Trans>
            Optional. Gavi reminds you; nothing is set aside on its own.
          </Trans>
        </Text>
      </View>

      <View style={{ gap: 8 }}>
        <Segmentado
          etiqueta={t('When to open it')}
          testId="travesia-abrir"
          estirar
          alto={40}
          valor={valor.abrir}
          onChange={abrir => onChange({ abrir })}
          opciones={[
            { valor: 'llegar', texto: t('Open on arrival') },
            { valor: 'hasta', texto: t('Keep sealed until…') },
          ]}
        />
        {valor.abrir === 'hasta' && (
          <input
            type="date"
            aria-label={t('Keep sealed until…')}
            data-testid="travesia-abrir-desde"
            value={valor.abrirDesde}
            min={minAbrir}
            onChange={e => onChange({ abrirDesde: e.target.value })}
            style={estiloCampo}
          />
        )}
      </View>
    </View>
  );
}
